var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const _IframeMessageManager = class _IframeMessageManager {
  handlers = /* @__PURE__ */ new Map();
  pendingRequests = /* @__PURE__ */ new Map();
  config;
  isDestroyed = false;
  channelId;
  constructor(config = {}) {
    this.config = {
      targetOrigin: "*",
      timeout: 1e4,
      enableLogging: false,
      maxRetries: 3,
      ...config
    };
    this.bindMessageListener();
    this.startCleanupTimer();
  }
  // 为每个实例指定唯一的channelId
  updateChannelId(channelId) {
    this.channelId = channelId;
  }
  // 绑定消息监听器
  bindMessageListener() {
    window.addEventListener("message", this.handleMessage);
  }
  // 处理接收到的消息
  handleMessage = /* @__PURE__ */ __name(async (event) => {
    if (this.isDestroyed) {
      return;
    }
    try {
      const message = event.data;
      if (!this.isValidMessage(message)) {
        return;
      }
      if (message.type === "__iframe_response__") {
        this.handleResponse(message);
        return;
      }
      const handler = this.handlers.get(message.type);
      if (handler) {
        try {
          const result = await handler(message.payload, message);
          this.sendResponse(event.source, message.id, true, result);
        } catch (error) {
          this.sendResponse(event.source, message.id, false, null, error.message);
        }
      } else {
        this.sendResponse(event.source, message.id, false, null, `No handler for message type: ${message.type}`);
      }
    } catch (error) {
      this.log("消息处理异常", error);
    }
  }, "handleMessage");
  // 验证消息格式
  isValidMessage(message) {
    return message && typeof message.id === "string" && typeof message.type === "string" && typeof message.timestamp === "number" && (!this.channelId || message.channelId === this.channelId);
  }
  // 发送响应
  sendResponse(target, messageId, success, data, error) {
    const response = {
      id: messageId,
      success,
      data,
      error,
      timestamp: Date.now()
    };
    const responseMessage = {
      id: this.generateId(),
      type: "__iframe_response__",
      payload: response,
      timestamp: Date.now(),
      channelId: this.channelId
      // 添加 channelId
    };
    target.postMessage(responseMessage, this.config.targetOrigin);
  }
  // 处理响应消息
  handleResponse(message) {
    const response = message.payload;
    const pendingRequest = this.pendingRequests.get(response.id);
    if (pendingRequest) {
      this.pendingRequests.delete(response.id);
      if (response.success) {
        pendingRequest.resolve(response.data);
      } else {
        pendingRequest.reject(new Error(response.error || "Unknown error"));
      }
    }
  }
  // 注册消息处理器
  on(messageType, handler) {
    this.handlers.set(messageType, handler);
  }
  // 移除消息处理器
  off(messageType) {
    this.handlers.delete(messageType);
  }
  // 发送消息并等待响应
  async send(target, messageType, payload, options = {}) {
    if (this.isDestroyed) {
      throw new Error("MessageManager has been destroyed");
    }
    const messageId = this.generateId();
    const timeout = options.timeout || this.config.timeout;
    const maxRetries = options.retries || this.config.maxRetries;
    const message = {
      id: messageId,
      type: messageType,
      payload,
      timestamp: Date.now(),
      channelId: this.channelId
      // 添加 channelId
    };
    return new Promise((resolve, reject) => {
      let timeoutId;
      const pendingRequest = {
        resolve: /* @__PURE__ */ __name((value) => {
          clearTimeout(timeoutId);
          resolve(value);
        }, "resolve"),
        reject: /* @__PURE__ */ __name((reason) => {
          clearTimeout(timeoutId);
          reject(reason);
        }, "reject"),
        timestamp: Date.now(),
        retries: 0
      };
      this.pendingRequests.set(messageId, pendingRequest);
      const attemptSend = /* @__PURE__ */ __name(() => {
        try {
          target.postMessage(message, this.config.targetOrigin);
          timeoutId = setTimeout(() => {
            const request = this.pendingRequests.get(messageId);
            if (request && request.retries < maxRetries) {
              request.retries++;
              attemptSend();
            } else {
              this.pendingRequests.delete(messageId);
              reject(new Error("Request timeout"));
            }
          }, timeout);
        } catch (error) {
          this.pendingRequests.delete(messageId);
          reject(error);
        }
      }, "attemptSend");
      attemptSend();
    });
  }
  // 发送单向消息（不等待响应）
  post(target, messageType, payload) {
    if (this.isDestroyed) return;
    const message = {
      id: this.generateId(),
      type: messageType,
      payload,
      timestamp: Date.now(),
      channelId: this.channelId
      // 添加 channelId
    };
    try {
      target.postMessage(message, this.config.targetOrigin);
    } catch (error) {
      this.log("post发送错误", error);
    }
  }
  // 清理超时的请求
  startCleanupTimer() {
    setInterval(() => {
      if (this.isDestroyed) return;
      const now = Date.now();
      const expiredRequests = [];
      this.pendingRequests.forEach((request, id) => {
        if (now - request.timestamp > this.config.timeout * this.config.maxRetries) {
          expiredRequests.push(id);
        }
      });
      expiredRequests.forEach((id) => {
        const request = this.pendingRequests.get(id);
        if (request) {
          this.pendingRequests.delete(id);
          request.reject(new Error("Request expired"));
        }
      });
    }, 5e3);
  }
  // 生成唯一ID
  generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  // 日志输出
  log(...args) {
    if (this.config.enableLogging) {
      console.log("[IframeMessageManager]", ...args);
    }
  }
  // 销毁实例
  destroy() {
    this.isDestroyed = true;
    this.handlers.clear();
    this.pendingRequests.forEach((request) => {
      request.reject(new Error("MessageManager destroyed"));
    });
    this.pendingRequests.clear();
    window.removeEventListener("message", this.handleMessage);
    this.log("销毁了");
  }
  // 获取统计信息
  getStats() {
    return {
      registeredHandlers: this.handlers.size,
      pendingRequests: this.pendingRequests.size,
      isDestroyed: this.isDestroyed
    };
  }
};
__name(_IframeMessageManager, "IframeMessageManager");
let IframeMessageManager = _IframeMessageManager;
const _IframeBridge = class _IframeBridge {
  messageManager;
  targetWindow = null;
  isReady = false;
  config;
  readyPromise;
  readyResolve;
  constructor(config = {}) {
    this.config = config;
    this.messageManager = new IframeMessageManager({
      enableLogging: true,
      ...config
    });
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
  }
  // iframe页面 设置准备就绪握手
  setupReadyHandshake() {
    this.messageManager.on("__iframe_handshake__", async (payload) => {
      await this.waitForReady();
      this.messageManager.updateChannelId(payload?.channelId);
      return { status: "connected" };
    });
  }
  // iframe页面，准备就绪
  async readyHandshake(targetWindow) {
    this.targetWindow = targetWindow;
    this.isReady = true;
    this.readyResolve();
  }
  // main页面，连接到目标窗口，并将此次唯一连接的channelId发送过去
  async connect(targetWindow, options) {
    options = options || {
      timeout: 1e3,
      retries: 5
    };
    this.targetWindow = targetWindow;
    const channelId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.messageManager.updateChannelId(channelId);
    try {
      await this.messageManager.send(
        targetWindow,
        "__iframe_handshake__",
        { channelId },
        options
      );
      this.isReady = true;
      this.readyResolve();
    } catch (error) {
      throw new Error(`Failed to connect to iframe: ${error.message}`);
    }
  }
  // 等待连接就绪
  async waitForReady() {
    return this.readyPromise;
  }
  // 注册API方法
  registerApi(methodName, handler) {
    this.messageManager.on(`api:${methodName}`, handler);
  }
  // 调用远程API
  async callApi(methodName, params) {
    if (!this.targetWindow) {
      throw new Error("No target window connected");
    }
    await this.waitForReady();
    return this.messageManager.send(this.targetWindow, `api:${methodName}`, params);
  }
  // 发送事件
  emit(eventName, data) {
    if (this.targetWindow) {
      this.messageManager.post(this.targetWindow, `event:${eventName}`, data);
    }
  }
  // 监听事件
  on(eventName, handler) {
    this.messageManager.on(`event:${eventName}`, (payload) => {
      handler(payload);
    });
  }
  // 销毁桥接
  destroy() {
    this.messageManager.destroy();
    this.targetWindow = null;
    this.isReady = false;
  }
  // 获取状态
  getStatus() {
    return {
      isReady: this.isReady,
      hasTargetWindow: !!this.targetWindow,
      stats: this.messageManager.getStats()
    };
  }
};
__name(_IframeBridge, "IframeBridge");
let IframeBridge = _IframeBridge;
export {
  IframeBridge,
  IframeMessageManager
};
//# sourceMappingURL=iframe.es.js.map
